import React, { useState } from 'react';
import { importAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle, Loader2, Users, Lock, FileText, Truck, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import RequireCap, { useCap } from '../components/auth/RequireCap';
import OnboardingImportTab from './OnboardingImportTab';
import VendorImportTab from './VendorImportTab';
import ClientImportTab from './ClientImportTab';
import JobImportTab from './JobImportTab';
import WinTeamEmployeeImportTab from './WinTeamEmployeeImportTab';
import WinTeamBankingImportTab from './WinTeamBankingImportTab';
import { useAuth } from '../context/AuthContext';

function ImportSection({ type, onUpload, templates, module: capModule }) {
  // Import is a bulk write — gate the file picker AND the upload button on
  // `<module>:add`. A view-only user without the cap should not even be able
  // to open the file dialog (backend ALSO 403s, but UX shouldn't lead them on).
  const canImport = useCap(capModule, 'add');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && (f.name.endsWith('.csv') || f.name.endsWith('.CSV'))) {
      setFile(f);
      setResult(null);
    } else {
      toast.error('Please upload a CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Select a file first'); return; }
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await onUpload(formData);
      setResult(res.data);
      if (res.data.created > 0) toast.success(`${res.data.created} records imported successfully`);
      if (res.data.errors?.length > 0) toast.error(`${res.data.errors.length} rows had errors`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async (category) => {
    try {
      const res = await importAPI.downloadTemplate(category);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${category}_import_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    }
  };

  return (
    <div className="space-y-6">
      {/* Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Step 1: Download Template</CardTitle>
          <CardDescription>Download a CSV template, fill in your data, then upload it below</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((t) => (
              <button
                key={t.category}
                onClick={() => downloadTemplate(t.category)}
                className="flex items-center gap-4 p-4 border-2 border-dashed border-slate-200 rounded-xl hover:border-brand-wordmark hover:bg-blue-50 transition-colors"
                data-testid={`download-${t.category}-template`}
              >
                <div className={`w-12 h-12 rounded-lg ${t.iconBg} flex items-center justify-center`}>
                  <t.icon className={`w-6 h-6 ${t.iconColor}`} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-800">{t.label}</p>
                  <p className="text-sm text-slate-500">{t.description}</p>
                </div>
                <Download className="w-5 h-5 text-slate-400 ml-auto" />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upload — gated by <module>:add. View-only users see a denied card
          and CANNOT open the file picker (backend 403s too, defense in depth). */}
      <Card>
        <CardHeader>
          <CardTitle>Step 2: Upload CSV File</CardTitle>
          <CardDescription>Select your completed CSV file and import</CardDescription>
        </CardHeader>
        <CardContent>
          {canImport ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <label className="flex-1 cursor-pointer">
                <div className={`flex items-center justify-center gap-3 p-6 border-2 border-dashed rounded-xl transition-colors ${file ? 'border-brand-wordmark bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <Upload className="w-6 h-6 text-slate-400" />
                  <div className="text-center">
                    {file ? (
                      <p className="font-medium text-brand-wordmark">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
                    ) : (
                      <>
                        <p className="font-medium text-slate-700">Click to select CSV file</p>
                        <p className="text-sm text-slate-500">or drag and drop</p>
                      </>
                    )}
                  </div>
                </div>
                <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" data-testid={`${type}-file-input`} />
              </label>
              <Button onClick={handleUpload} disabled={!file || uploading} className="btn-navy h-12 px-6" data-testid={`${type}-upload-btn`}>
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? 'Importing...' : 'Import'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-6 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/60" data-testid={`${type}-import-denied`}>
              <Lock className="w-6 h-6 text-slate-400" />
              <div>
                <p className="font-medium text-slate-700">Import not available for your role</p>
                <p className="text-sm text-slate-500">Bulk import requires the <code className="px-1 py-0.5 bg-slate-200 rounded text-xs">{capModule}:add</code> capability. Ask an administrator to grant it if you need to bulk-import.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card data-testid={`${type}-results`}>
          <CardHeader>
            <CardTitle>Import Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold">{result.total}</p>
                <p className="text-sm text-slate-500">Total Rows</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-600">{result.created}</p>
                <p className="text-sm text-slate-500">Imported</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-600">{result.errors?.length || 0}</p>
                <p className="text-sm text-slate-500">Errors</p>
              </div>
            </div>

            {result.errors?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-sm text-red-600 mb-2">Errors</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.errors.map((err, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono">{err.row}</TableCell>
                        <TableCell className="text-red-600">{err.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {result.created > 0 && result.errors?.length === 0 && (
              <div className="flex items-center gap-2 text-emerald-600 mt-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">All records imported successfully!</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function ImportPage() {
  // WinTeam HR/payroll imports carry SSNs and bank accounts — admin only.
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const assetTemplates = [
    { category: 'it_assets', label: 'IT Assets Template', description: 'Laptops, phones with serial numbers, IMEI, etc.', icon: FileSpreadsheet, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600' },
    { category: 'uniforms', label: 'Uniforms / Equipment Template', description: 'Uniforms, equipment, safety gear, accessories', icon: FileSpreadsheet, iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
  ];

  return (
    <div className="space-y-6" data-testid="import-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Import Data</h1>
          <p className="text-slate-500 mt-1">Bulk import inventory items and WinTeam employee data from spreadsheets</p>
        </div>
      </div>

      <Tabs defaultValue="assets" className="space-y-6">
        <TabsList>
          <TabsTrigger value="assets" data-testid="import-assets-tab">Assets (CSV)</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="import-onboarding-tab"><FileText className="w-4 h-4 mr-1.5" /> Onboarding Packet (PDF)</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="import-vendors-tab"><Truck className="w-4 h-4 mr-1.5" /> Vendor Master (WinTeam)</TabsTrigger>
          <TabsTrigger value="clients" data-testid="import-clients-tab"><Building2 className="w-4 h-4 mr-1.5" /> Customer Master (WinTeam)</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="import-jobs-tab"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Jobs + Rate Lines (WinTeam)</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="winteam-employees" data-testid="import-winteam-employees-tab">
              <Users className="w-4 h-4 mr-1.5" /> Employee Master (WinTeam)
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="winteam-banking" data-testid="import-winteam-banking-tab">
              <Lock className="w-4 h-4 mr-1.5" /> Direct Deposit (NACHA)
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="assets">
          <ImportSection
            type="assets"
            module="assets"
            onUpload={importAPI.importItems}
            templates={assetTemplates}
          />
        </TabsContent>

        <TabsContent value="onboarding">
          <OnboardingImportTab />
        </TabsContent>

        <TabsContent value="vendors">
          <VendorImportTab />
        </TabsContent>

        <TabsContent value="clients">
          <ClientImportTab />
        </TabsContent>

        <TabsContent value="jobs">
          <JobImportTab />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="winteam-employees">
            <WinTeamEmployeeImportTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="winteam-banking">
            <WinTeamBankingImportTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

